import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";
import type { PageFileChange, PageScreenshot, PageSuggestion, PageValidationReport } from "@ncp/page-host";

const BASE = "/api/platform/pages";
const enc = encodeURIComponent;

export type PageStatus = "Draft" | "InReview" | "Published" | "Retired";

export interface PageSummary {
  id: string;
  key: string;
  version: number;
  status: PageStatus;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  spaces: string[];
  slots: string[];
  icon: string;
  updatedAt: string;
  bundleHash?: string | null;
  builtAt?: string | null;
  fileCount: number;
  sizeBytes: number;
  visibility: "Space" | "Personal";
  ownerUserId?: string | null;
  origin: string;
  tags: string[];
  publishedAt?: string | null;
  validationStatus?: string | null;
  validatedAt?: string | null;
}

export interface PageFileInfo { path: string; sizeBytes: number; isBinary: boolean; contentType: string; sha256: string }
export interface PageFile { path: string; content: string; isBinary: boolean }
export interface PageIssue { path: string; error: string }

export interface PageAgentEval { name: string; brief: string; state?: unknown; expect?: Record<string, unknown> }
export interface PageAgentSpec {
  key: string;
  name?: { en?: string; ar?: string } | string | null;
  tier?: string | null;
  model?: string | null;
  instructions?: string | null;
  tools?: string[];
  knowledge?: string[];
  guardrails?: string | null;
  suggestions?: unknown[];
  greeting?: { en?: string; ar?: string } | null;
  evals?: PageAgentEval[];
  tokensPerRun?: number | null;
  monthlyBudgetUsd?: number | null;
}
export interface PageJobSpec {
  key: string;
  title?: string | null;
  titleAr?: string | null;
  schedule?: string | null;
  on?: { event: string; playbook?: string | null; state?: string | null; series?: string | null; tags?: string[] | null } | null;
  run: { agent?: string | null; function?: string | null; model?: string | null; brief?: string | null; input?: unknown };
  owner?: string | null;
  digest?: boolean;
}
export interface PageCollectionSpec { key: string; name?: string; scope?: string; classification?: string; retentionDays?: number | null; schema?: unknown }

export interface PageBuildReport {
  at?: string;
  ok?: boolean;
  diagnostics?: Array<{ severity: string; message: string; file?: string | null; line?: number | null; column?: number | null }>;
  report?: { outputs?: Array<{ path: string; bytes: number }>; builder?: string; totalBytes?: number; [k: string]: unknown };
}

export interface PageDetail {
  page: PageSummary;
  files: PageFileInfo[];
  manifest: Record<string, unknown> | null;
  agents: PageAgentSpec[] | null;
  jobs: PageJobSpec[] | null;
  functions: Array<{ name: string; [k: string]: unknown }> | null;
  collections: PageCollectionSpec[] | null;
  clientTools: Array<{ name: string; description?: string }> | null;
  buildReport: PageBuildReport | null;
  issues: PageIssue[];
  versions: PageSummary[];
  validation: PageValidationReport | null;
  source: string;
}

export interface PageTemplate { key: string; name: string; description: string }
export interface NewPageInput { key: string; title: string; titleAr?: string | null; space?: string | null; template?: string | null; slots?: string[] | null; personal?: boolean; description?: string | null }
export interface PageFilesPatch { upserts?: Array<{ path: string; content: string; isBinary?: boolean }>; deletes?: string[]; changeNote?: string | null }
export interface PageBuildOutcome { ok: boolean; bundleHash?: string | null; diagnostics: Array<{ severity: string; message: string; file?: string | null; line?: number | null }>; reused: boolean; issues: PageIssue[] }
export interface PageStageResult { changeSetId: string; changeSetTitle: string; items: string[]; agentDefinitionIds: string[] }
export interface PageExport { key: string; version: number; status: string; spaces: string[]; source: string; files: PageFile[] }

export interface PageUsage {
  since: string;
  users: number;
  byDay: Array<{ day: string; kind: string; count: number }>;
  bySpace: Array<{ space: string | null; opens: number }>;
  calls: Array<{ capability: string; outcome: string; count: number; avgMs: number }>;
  agents: Array<{ agent: string; calls: number; costUsd: number }>;
  byUser: Array<{ userId: string; name?: string | null; opens: number; calls: number; turns: number; tokens: number; costUsd: number }>;
  budget: { monthlyUsd?: number | null; perUserMonthlyUsd?: number | null; spentUsd: number; since: string } | null;
}

export interface PageInvocation { id: string; capability: string; sideEffect: string; caller: string; userName?: string | null; pageVersion?: number | null; spaceKey?: string | null; agentKey?: string | null; jobKey?: string | null; outcome: string; error?: string | null; durationMs: number; summary?: string | null; at: string }

export interface PageData {
  collections: Array<{ key: string; name: string; scope: string; classification: string; retentionDays?: number | null; spaces: string[]; records: number; people: number; lastAt?: string | null;
    latest: Array<{ id: string; key?: string | null; owner?: string | null; space?: string | null; revision: number; at: string; data: unknown }> }>;
  datasets: Array<{ key: string; name: string; nameAr?: string | null; space?: string | null; rows: number; origin: string; classification: string;
    columns: Array<{ key: string; label?: string; labelAr?: string; type?: string; unit?: string }> | null; lineage: Array<{ kind?: string; ref?: string; title?: string; url?: string; retrievedAt?: string }> | null;
    refreshedAt?: string | null; updatedAt: string; sample: Array<Record<string, unknown>> | null }>;
  reads: { since: string; byCapability: Array<{ capability: string; outcome: string; count: number }>; recent: Array<{ capability: string; summary?: string | null; caller: string; agentKey?: string | null; outcome: string; at: string }> };
}

export interface PageJobRunInfo { id: string; status: string; at: string; durationMs: number; error?: string | null; triggeredBy?: string | null; eventType?: string | null }
export interface PageJobInfo {
  id: string; job: string; automationKey: string; name: string; enabled: boolean; pausedReason?: string | null; trigger: string; triggerConfig: Record<string, unknown> | null;
  nextRunAt?: string | null; lastRunAt?: string | null; runCount: number; failureCount: number; owner?: string | null; space?: string | null; source?: string | null; runs: PageJobRunInfo[];
}

export interface ValidationState { report: PageValidationReport | null; source: string; stale: boolean }

export interface ApprovedPackage { id: string; name: string; version: string; status: "Requested" | "Approved" | "Rejected" | string; licence?: string | null; category?: string | null; purpose?: string | null;
  vendor: boolean; sizeBytes?: number | null; requestedForPageKey?: string | null; requestedByName?: string | null; decidedByName?: string | null; decidedAt?: string | null; decisionNote?: string | null }
export interface PackagesInfo { builder: { available: boolean; version?: string | null }; store: unknown; packages: ApprovedPackage[] }
export interface CapabilityRequest { id: string; title: string; spec: string; draftSchemaJson?: string | null; status: "Open" | "Accepted" | "Declined" | "Delivered" | string; pageKey?: string | null;
  spaceKey?: string | null; requestedByName?: string | null; resolution?: string | null; createdAt: string; updatedAt: string }

// ---------------------------------------------------------------- queries

export const pageKeys = {
  all: ["pages"] as const,
  list: (personal: boolean) => ["pages", "list", personal] as const,
  detail: (key: string) => ["pages", "detail", key] as const,
  version: (id: string) => ["pages", "version", id] as const,
  file: (id: string, path: string) => ["pages", "file", id, path] as const,
};

export function usePages(includePersonal = false) {
  const api = useApi();
  return useQuery({ queryKey: pageKeys.list(includePersonal), queryFn: () => api.get<PageSummary[]>(`${BASE}?includePersonal=${includePersonal}`) });
}

export function usePageThumbnails(locale: string, keys: string[]) {
  const api = useApi();
  const list = [...keys].sort().join(",");
  return useQuery({
    queryKey: ["pages", "thumbnails", locale, list],
    queryFn: () => api.get<Record<string, { version: number; url: string; at: string }>>(`${BASE}/thumbnails?locale=${locale}&keys=${enc(list)}`),
    enabled: keys.length > 0,
    staleTime: 60_000,
  });
}

export function usePageTemplates() {
  const api = useApi();
  return useQuery({ queryKey: ["pages", "templates"], queryFn: () => api.get<PageTemplate[]>(`${BASE}/templates`), staleTime: Infinity });
}

/** The newest version of a page (its draft, else the published one). */
export function usePage(key: string) {
  const api = useApi();
  return useQuery({ queryKey: pageKeys.detail(key), queryFn: () => api.get<PageDetail>(`${BASE}/${enc(key)}`), enabled: !!key });
}

export function usePageVersion(id: string | null) {
  const api = useApi();
  return useQuery({ queryKey: pageKeys.version(id ?? ""), queryFn: () => api.get<PageDetail>(`${BASE}/versions/${id}`), enabled: !!id });
}

export function usePageFile(id: string, path: string | null, binary = false) {
  const api = useApi();
  return useQuery({
    queryKey: pageKeys.file(id, path ?? ""),
    queryFn: () => api.get<PageFile>(`${BASE}/versions/${id}/files/${path!.split("/").map(enc).join("/")}`),
    enabled: !!path && !binary,
    staleTime: Infinity,
  });
}

export function usePageValidation(id: string) {
  const api = useApi();
  return useQuery({ queryKey: ["pages", "validation", id], queryFn: () => api.get<ValidationState>(`${BASE}/versions/${id}/validation`) });
}

export function usePageScreenshots(id: string) {
  const api = useApi();
  return useQuery({ queryKey: ["pages", "screenshots", id], queryFn: () => api.get<Partial<Record<"en" | "ar", PageScreenshot>>>(`${BASE}/versions/${id}/screenshots`) });
}

export function usePageUsage(key: string, days: number) {
  const api = useApi();
  return useQuery({ queryKey: ["pages", "usage", key, days], queryFn: () => api.get<PageUsage>(`${BASE}/${enc(key)}/usage?days=${days}`) });
}

export function usePageInvocations(key: string) {
  const api = useApi();
  return useQuery({ queryKey: ["pages", "invocations", key], queryFn: () => api.get<PageInvocation[]>(`${BASE}/${enc(key)}/invocations?take=100`) });
}

export function usePageData(key: string) {
  const api = useApi();
  return useQuery({ queryKey: ["pages", "data", key], queryFn: () => api.get<PageData>(`${BASE}/${enc(key)}/data`) });
}

export function usePageJobs(key: string) {
  const api = useApi();
  return useQuery({ queryKey: ["pages", "jobs", key], queryFn: () => api.get<PageJobInfo[]>(`${BASE}/${enc(key)}/jobs`) });
}

export function usePageSuggestions(key: string) {
  const api = useApi();
  return useQuery({ queryKey: ["pages", "suggestions", key], queryFn: () => api.get<PageSuggestion[]>(`${BASE}/${enc(key)}/suggestions`) });
}

export function useSuggestionInbox(decided = false) {
  const api = useApi();
  return useQuery({ queryKey: ["pages", "suggestions", "inbox", decided], queryFn: () => api.get<PageSuggestion[]>(`${BASE}/suggestions/inbox${decided ? "?decided=true" : ""}`) });
}

export function useSuggestionDiff(id: string | null) {
  const api = useApi();
  return useQuery({ queryKey: ["pages", "suggestion-diff", id], queryFn: () => api.get<PageFileChange[]>(`/api/pages/suggestions/${id}/diff`), enabled: !!id });
}

export function usePagePackages() {
  const api = useApi();
  return useQuery({ queryKey: ["pages", "packages"], queryFn: () => api.get<PackagesInfo>(`${BASE}/packages`) });
}

export function useCapabilityRequests() {
  const api = useApi();
  return useQuery({ queryKey: ["pages", "capability-requests"], queryFn: () => api.get<CapabilityRequest[]>(`${BASE}/capability-requests`) });
}

// ---------------------------------------------------------------- mutations

/** Everything that changes a page project, each refreshing what it touches. */
export function usePageActions() {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useTranslation();
  // A build, a validation run or a job started is not a saved change: its failure is not headed "Not saved".
  const notDone = t("admin:common.error");
  const refresh = () => qc.invalidateQueries({ queryKey: pageKeys.all });
  return {
    create: useSave({ mutationFn: (input: NewPageInput) => api.post<PageDetail>(BASE, input), onSuccess: refresh }),
    draft: useSave({ mutationFn: (key: string) => api.post<PageDetail>(`${BASE}/${enc(key)}/draft`, {}), onSuccess: refresh }),
    save: useSave({
      mutationFn: ({ id, patch }: { id: string; patch: PageFilesPatch }) => api.put<PageDetail>(`${BASE}/versions/${id}/files`, patch),
      onSuccess: (d) => { qc.setQueryData(pageKeys.detail(d.page.key), d); void qc.invalidateQueries({ queryKey: ["pages", "validation", d.page.id] }); void qc.invalidateQueries({ queryKey: ["pages", "file", d.page.id] }); },
    }),
    build: useSave({ mutationFn: ({ id, force }: { id: string; force?: boolean }) => api.post<PageBuildOutcome>(`${BASE}/versions/${id}/build${force ? "?force=true" : ""}`, {}), failed: notDone, onSuccess: refresh }),
    validate: useSave({
      mutationFn: ({ id, mode }: { id: string; mode: "quick" | "full" }) => api.post<PageValidationReport>(`${BASE}/versions/${id}/validate${mode === "quick" ? "?mode=quick" : ""}`, mode === "quick" ? {} : { build: true, render: true, evals: true }),
      failed: notDone,
      onSuccess: () => refresh(),
    }),
    stage: useSave({ mutationFn: (id: string) => api.post<PageStageResult>(`${BASE}/versions/${id}/stage`, {}), onSuccess: refresh }),
    discard: useSave({ mutationFn: (id: string) => api.delete<void>(`${BASE}/versions/${id}`), onSuccess: refresh }),
    exportJson: (id: string) => api.get<PageExport>(`${BASE}/versions/${id}/export?format=json`),
    exportZip: (id: string, fileName: string) => api.download(`${BASE}/versions/${id}/export`, fileName),
    accept: useSave({ mutationFn: ({ id, note }: { id: string; note?: string }) => api.post<PageSuggestion>(`${BASE}/suggestions/${id}/accept`, { note: note || null }), onSuccess: refresh }),
    decline: useSave({ mutationFn: ({ id, note }: { id: string; note?: string }) => api.post<PageSuggestion>(`${BASE}/suggestions/${id}/decline`, { note: note || null }), onSuccess: refresh }),
    decidePackage: useSave({
      mutationFn: ({ id, approve, note, licence }: { id: string; approve: boolean; note?: string; licence?: string }) => api.post(`${BASE}/packages/${id}/decide`, { approve, note: note || null, licence: licence || null }),
      onSuccess: refresh,
    }),
    resolveCapability: useSave({
      mutationFn: ({ id, status, resolution }: { id: string; status: string; resolution?: string }) => api.post(`${BASE}/capability-requests/${id}/resolve`, { status, resolution: resolution || null }),
      onSuccess: refresh,
    }),
    runJob: useSave({ mutationFn: (automationId: string) => api.post(`/api/platform/automations/${automationId}/run`, { dryRun: false }), failed: notDone, onSuccess: refresh }),
  };
}

// ---------------------------------------------------------------- spaces

export interface SpaceText { en: string; ar?: string | null }
export interface SpaceHomeSection { kind: "builtin" | "page" | "widget" | "use-cases" | string; key?: string | null; page?: string | null; widget?: string | null; title?: SpaceText | null; span?: number }
export interface SpaceNavEntry { key: string; page?: string | null; builtin?: string | null; label?: SpaceText | null; icon?: string | null; section: "work" | "insight" | "resources" | string; permission?: string | null; group?: string | null }
export interface SpaceMatterTab { builtin?: string | null; page?: string | null; label?: SpaceText | null; icon?: string | null; when?: string | null }
export interface SpaceTabGroup { stage: string; label?: SpaceText | null; tabs: SpaceMatterTab[]; states?: string[] | null }
export interface SpaceUseCase { key: string; title: SpaceText; description?: SpaceText | null; icon?: string | null; playbook?: string | null; page?: string | null; prompt?: string | null;
  parameters?: Record<string, unknown> | null; produces?: SpaceText | null; duration?: string | null; tags?: string[] | null; status?: string | null }
export interface SpaceManifest {
  version?: number;
  source?: string;
  theme?: string | null;
  vocabulary?: string | null;
  home: SpaceHomeSection[];
  nav: SpaceNavEntry[];
  matterTabs: Record<string, SpaceTabGroup[]>;
  workbench: string[];
  useCases: SpaceUseCase[];
  grants: Record<string, string[]>;
  mounts: string[];
}

export interface SpaceUsage {
  key: string; days: number; people: number; opens: number;
  pages: Array<{ key: string; name?: string | null; opens: number; people: number; agentTurns: number; calls: number; errors: number; jobs: number; exports: number; refused: number; suggestions: Record<string, number> }>;
  byDay: Array<{ day: string; opens: number; people: number }>;
  bySlot: Array<{ slot: string | null; opens: number }>;
}

export function useSpaceManifest(key: string) {
  const api = useApi();
  return useQuery({ queryKey: ["spaces", "manifest", key], queryFn: () => api.get<{ key: string; manifest: SpaceManifest; issues: string[] }>(`/api/platform/spaces/${enc(key)}/manifest`) });
}

export function useSpaceUsage(key: string, days: number) {
  const api = useApi();
  return useQuery({ queryKey: ["spaces", "usage", key, days], queryFn: () => api.get<SpaceUsage>(`/api/platform/spaces/${enc(key)}/usage?days=${days}`) });
}

export function useSaveSpaceManifest(key: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useSave({
    mutationFn: (manifest: SpaceManifest) => api.put<SpaceManifest>(`/api/platform/spaces/${enc(key)}/manifest`, manifest),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["spaces", "manifest", key] }); void qc.invalidateQueries({ queryKey: ["platform", "workspaces"] }); },
  });
}

/** A label in the reader's language. */
export function text(t: SpaceText | { en?: string | null; ar?: string | null } | string | null | undefined, locale: string): string {
  if (!t) return "";
  if (typeof t === "string") return t;
  return (locale === "ar" && t.ar) || t.en || t.ar || "";
}
